import * as React from 'react';
import { createFileRoute, Link, useParams } from '@tanstack/react-router';
import { Archive, LayoutGrid, Pencil } from 'lucide-react';
import { Spinner } from '@/components/ui/spinner';
import { ErrorMessage } from '@/components/ui/error-message';
import { Button } from '@/components/ui/button';
import { EngagementHero } from '@/components/engagement/hero';
import { FactsStrip, type FactItem } from '@/components/engagement/facts-strip';
import { HealthPostureGrid } from '@/components/engagement/health-posture-grid';
import { CriticalSignals } from '@/components/engagement/critical-signals';
import { SubmittalVerbsCard } from '@/components/engagement/submittal-verbs-card';
import { ScorecardSummary } from '@/components/engagement/scorecard-summary';
import { SnapshotsList } from '@/components/engagement/snapshots-list';
import { DiscoveryStatusCard } from '@/components/engagement/discovery-status-card';
import { SelectionStatusCard } from '@/components/engagement/selection-status-card';
import { requirementSourceCounts } from '@/components/engagement/module-status-logic';
import { DoNextCard } from '@/components/engagement/do-next-card';
import { StageTrack } from '@/components/engagement/stage-track';
import {
  overviewActions,
  overviewStages,
  scheduleOf,
  type OverviewInput,
} from '@/components/engagement/overview-plan';
import { GoLiveRunway } from '@/components/ui/charts';
import { todayIso } from '@/components/discovery/discovery-logic';
import { ArchiveModal } from '@/components/modals/archive-modal';
import { PdfPreviewModal } from '@/components/modals/pdf-preview-modal';
import { CaptureSnapshotModal } from '@/components/modals/capture-snapshot-modal';
import { EditEngagementDetailsModal } from '@/components/modals/edit-engagement-details-modal';
import { DeleteEngagementControl } from '@/components/engagement/delete-engagement-control';
import { useEngagementQuery } from '@/lib/api/engagements';
import { useReadinessScorecardQuery } from '@/lib/api/readiness';
import { rollupColorToPostureStatus, useOverallPostureQuery } from '@/lib/api/health-posture';
import { factValue, useContractFactsQuery } from '@/lib/api/contract-facts';
import { useEngagementActivityQuery } from '@/lib/api/engagement-activity';
import { useDiscoverySummaryQuery } from '@/lib/api/discovery';
import { useEngagementRequirementsQuery } from '@/lib/api/engagement-requirements';
import { useSignoffQuery } from '@/lib/api/requirements-signoff';
import { useVendorIntelligenceQuery } from '@/lib/api/vendor-intelligence';
import { useVendorReportQuery } from '@/lib/api/vendor-intelligence-report';
import { ContractAttentionPanel } from '@/components/contract/attention-panel';
import { LatestPulseCard } from '@/components/engagement/latest-pulse-card';
import { useRequestPdfRenderMutation, useSnapshotListQuery } from '@/lib/api/snapshots';
import { hasIvvModule, serviceLabel } from '@/lib/engagement-modules';
import { formatDate, titleCase } from '@/lib/utils/format';

/**
 * Engagement overview (C20-132 / prototype-parity rewrite of T-0097).
 *
 * Prototype contract: `design_handoff_caliber_v2/prototype/screens-
 * engagement.js → window.Screens.overview`. Every element on this page is
 * sourced from a real backend read; gaps say so in plain words rather than
 * show mock data. Source pills name where a number comes from in the
 * reader's terms ("Planned go-live", "AI proposals"), never a ticket number
 * or a field name (C2-29), and every date goes through `formatDate` (C2-45).
 *
 * Two variants, chosen by the engagement's modules (`lib/engagement-modules`):
 *
 * IV&V (any of Contract Compliance, Health Check, Go-Live Readiness), the
 * prototype's screen:
 *   1. Hero (.eng-hero) — background image, posture wrapper class,
 *      crumb (client · slug · IV&V by firm), posture pill, title, subtitle
 *      (phaseHint + vendor), Capture + Generate-PDF CTAs.
 *   2. Facts strip (.eng-facts) — 6 columns:
 *        Go-Live · Phase · Partner · Budget used · Open · overdue · AI proposals
 *   3. Row 1: Health-posture grid (grow) + [Critical signals + Submittal verbs] (380px col)
 *   4. Row 2: Scorecard bars (grow) + Snapshots list (380px)
 *   When Discovery or Selection is on as well, their status cards sit
 *   between the contract panel and the posture grid.
 *
 * Discovery and Selection only (no IV&V module; City of Everett, 22 October
 * 2026; drawn as graphics 5 November 2026): the IV&V surfaces would read
 * like an IV&V project, so the page keeps the hero (crumb "Discovery &
 * Selection by firm", no posture pill: posture is an IV&V roll-up), then
 *   1. a strip of the schedule (start to planned end, today marked, the
 *      days left), the phase and the main POC, each editable;
 *   2. Do next: the work waiting, in the order `overviewActions` gives
 *      (blocked, to do, waiting, check), each with one button to where it
 *      is done;
 *   3. the stage track (`overviewStages`): Interviews and Areas covered
 *      under Discovery, Requirements, Ratings, Shortlist and Report under
 *      Selection (with Vendors when Discovery is off);
 *   4. the Discovery and Selection cards: sessions and vendor ratings as
 *      squares, each area's source meter, requirements by criticality;
 *   5. what changed recently and the snapshots, as before.
 * No IV&V read is made. Both lists are pure functions over reads the
 * Discovery and Selection screens already make; nothing is a new number.
 *
 * Element-by-element wire-up matrix (see PR body for full table):
 *   Hero banner image          → engagement.banner_image_url (T-0091)
 *   Hero crumb client / slug   → engagement.client_name (slugified client-side)
 *   Hero posture pill          → useOverallPostureQuery (T-0072), IV&V only
 *   Hero title                 → engagement.name
 *   Hero subtitle phaseHint    → engagement.phase_path.find(current) (T-0119)
 *   Hero subtitle vendor       → confirmed contract facts (IV&V); the
 *                                Discovery framework or the requirement
 *                                count otherwise
 *   Hero CTAs                  → snapshots + PDF mutations (T-0086 / T-0101)
 *   Facts: Go-Live             → engagement.planned_end (T-0128), editable
 *   Facts: Phase               → engagement.phase_path (T-0119) — derived
 *                                from engagement status, editable
 *   Facts: Main POC            → engagement.client_pm (T-0128), editable.
 *                                Labelled "Partner" in the prototype; renamed
 *                                at the product owner's request because the
 *                                field holds the client's main point of
 *                                contact, not an Avero partner.
 *   Facts: Budget used         → engagement.budget_rollup (T-0074 via T-0119)
 *   Facts: Open · overdue      → engagement.open_issues_count (T-0119) ·
 *                                engagement.overdue_count (past-due deliverables)
 *   Facts: AI proposals        → useProposalQueueQuery (T-0122)
 *   Facts: Schedule            → engagement.start_date / planned_end (non-IV&V)
 *   Do next, stage track       → the Discovery summary, the sign-off status,
 *                                the requirement list, the vendor
 *                                intelligence evaluation and its report
 *   Health-posture grid        → useOverallPostureQuery per_domain (T-0072)
 *   Critical signals           → engagement.recent_signals (T-0119)
 *   Submittal verbs            → useEdlListQuery aggregation (T-0065 / T-0102)
 *   Scorecard bars + total     → useReadinessScorecardQuery (T-0086)
 *   Discovery status card      → useDiscoverySummaryQuery (T-0185)
 *   Selection status card      → the requirement list and the vendor evaluation
 *   Snapshots                  → useSnapshotListQuery (T-0101)
 *
 * Gaps surface as Pending copy (never invented data):
 *   - "vendor pending" subtitle hint when no contract baseline carries a vendor.
 *   - Main POC is entered by hand; it is not read from the contract.
 *
 * Editing: the Go-Live, Phase and Main POC tiles carry a pencil that opens
 * `EditEngagementDetailsModal` (PATCH /projects/:id). Before that modal
 * these tiles were frozen at the wizard's values and Phase never left Setup.
 * The same modal changes the engagement's modules (Discovery, Selection,
 * ...); the "Details and modules" button next to the archive one opens it,
 * since a pencil on a date tile is no place to look for module switches.
 *
 * Capture snapshot: the hero CTA opens `CaptureSnapshotModal` locked to
 * the `engagement` type, which freezes this whole overview picture
 * (facts, posture, scorecard, budget, deliverables) and states the side
 * effects — permanence, notifications, queued AI re-eval — before commit.
 *
 * Delete engagement (T-0161): firm admins see it next to "Build engagement
 * archive"; it opens the typed-name confirmation and lands on the portfolio.
 */
export const Route = createFileRoute('/engagements/$engagementId/')({
  component: OverviewPage,
});

function OverviewPage() {
  const { engagementId } = useParams({ from: '/engagements/$engagementId/' });

  const engagementQuery = useEngagementQuery(engagementId);
  const modules = engagementQuery.data?.modules_enabled ?? [];
  const ivvOn = engagementQuery.data ? hasIvvModule(modules) : false;
  const discoveryOn = modules.includes('discovery');
  const selectionOn = modules.includes('selection');

  // The IV&V reads (scorecard, posture, contract facts, the pending
  // proposals count) are made only for an engagement with an IV&V module;
  // for the others they would answer for things the engagement does not do.
  const ivvId = ivvOn ? engagementId : undefined;
  const scorecardQuery = useReadinessScorecardQuery(ivvId);
  const postureQuery = useOverallPostureQuery(ivvId);
  const snapshotsQuery = useSnapshotListQuery(engagementId, { limit: 5 });
  // T-0122: pending-proposals count feeds the AI proposals KPI. Filter at
  // the server (`decision: 'pending'`) so we don't ship the full queue
  // payload across the wire just to count the unreviewed rows.
  const activityQuery = useEngagementActivityQuery(ivvId);
  // Confirmed contract facts (vendor, value, contracted go-live) read from
  // the contract documents.
  const contractFacts = useContractFactsQuery(ivvId).data;
  // The Discovery summary and the requirement list, read only when their
  // module is on; the status cards below share these cached reads.
  const discoveryQuery = useDiscoverySummaryQuery(engagementId, discoveryOn);
  const requirementsQuery = useEngagementRequirementsQuery(engagementId, selectionOn);
  // What Do next and the stage track read besides: the sign-off (it exists
  // once Discovery has started), the vendor evaluation and its report. Only
  // the Discovery and Selection overview shows them, so only it reads them.
  const planOn = engagementQuery.data ? !ivvOn : false;
  const discoveryStarted = discoveryQuery.data?.started === true;
  const signoffOn = planOn && discoveryOn && discoveryStarted;
  const viOn = planOn && selectionOn;
  const signoffQuery = useSignoffQuery(engagementId, signoffOn);
  const viQuery = useVendorIntelligenceQuery(engagementId, { enabled: viOn });
  const reportQuery = useVendorReportQuery(engagementId, viOn);

  const pdfMutation = useRequestPdfRenderMutation(engagementId);

  // Modal triggers — see T-0115 architecture notes for why these live at
  // the overview level instead of a route boundary.
  const [archiveOpen, setArchiveOpen] = React.useState(false);
  const [pdfPreviewSnapshotId, setPdfPreviewSnapshotId] = React.useState<string | null>(null);
  const [captureOpen, setCaptureOpen] = React.useState(false);
  const [editDetailsOpen, setEditDetailsOpen] = React.useState(false);

  // Snapshot capture — opens the confirmation modal (engagement-level
  // snapshot). The modal owns the mutation, the side-effect disclosure and
  // the result state; nothing is written until the user confirms there.
  const handleCaptureSnapshot = React.useCallback(() => setCaptureOpen(true), []);

  // PDF generation — operates on the latest snapshot. If there isn't
  // one, kick off a capture first; the mutation chain re-resolves on
  // the snapshot query invalidation.
  const latestSnapshotId = snapshotsQuery.data?.items?.[0]?.id;
  const handleGeneratePdf = React.useCallback(() => {
    if (!latestSnapshotId || pdfMutation.isPending) return;
    const idempotencyKey =
      typeof globalThis.crypto?.randomUUID === 'function'
        ? globalThis.crypto.randomUUID()
        : `pdf-${Date.now()}`;
    // Open the preview for that snapshot once the render is requested: it
    // shows the PDF's progress and the finished file. Errors surface below
    // the hero instead of vanishing.
    pdfMutation.mutate(
      { snapshotId: latestSnapshotId, idempotencyKey },
      { onSuccess: () => setPdfPreviewSnapshotId(latestSnapshotId) },
    );
  }, [latestSnapshotId, pdfMutation]);

  if (engagementQuery.isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-ink-500">
        <Spinner /> Loading engagement…
      </div>
    );
  }

  if (engagementQuery.error) {
    return <ErrorMessage error={engagementQuery.error} />;
  }

  if (!engagementQuery.data) {
    return <ErrorMessage error={new Error('Engagement not found.')} />;
  }

  const engagement = engagementQuery.data;
  const contractEnabled = engagement.modules_enabled.includes('contract_compliance');
  const posture = rollupColorToPostureStatus(postureQuery.data?.overall_rollup);

  // ----- Subtitle: "Phase hint · vendor X" -----
  // `phase_path` is the engagement-detail DTO field (T-0119) carrying
  // [{label, status: 'done'|'current'|'upcoming'}]. We use the current
  // step's label to look up the prototype's pre-canned phaseHint copy.
  const currentPhaseStep = engagement.phase_path.find((s) => s.status === 'current');
  const currentPhase = currentPhaseStep?.label ?? titleCase(engagement.status);
  // Vendor comes from the confirmed contract facts (read from the SOW by AI
  // and approved, or typed on the Contract baseline tab).
  const vendorName = factValue(contractFacts, 'vendor_name');
  const discoverySummary = discoveryQuery.data;
  const requirementCounts = requirementSourceCounts(requirementsQuery.data?.items ?? []);
  const subtitle = ivvOn ? (
    <>
      {currentPhase} ·{' '}
      {vendorName ? (
        <>
          vendor <strong>{vendorName}</strong>
        </>
      ) : (
        <Link
          to="/engagements/$engagementId/contract/baseline"
          params={{ engagementId }}
          className="underline decoration-ink-300 underline-offset-2 hover:decoration-ink-700"
        >
          vendor not identified yet
        </Link>
      )}
    </>
  ) : (
    <>
      {currentPhase} ·{' '}
      {discoveryOn ? (
        discoverySummary?.framework ? (
          <>
            <strong>{discoverySummary.framework.name}</strong> framework v
            {discoverySummary.framework.version}
          </>
        ) : discoverySummary && !discoverySummary.started ? (
          <Link
            to="/engagements/$engagementId/discovery/setup"
            params={{ engagementId }}
            className="underline decoration-ink-300 underline-offset-2 hover:decoration-ink-700"
          >
            Discovery not set up yet
          </Link>
        ) : (
          'Discovery'
        )
      ) : requirementsQuery.data ? (
        `${requirementCounts.total.toLocaleString('en-US')} ${
          requirementCounts.total === 1 ? 'requirement' : 'requirements'
        }`
      ) : (
        'Selection'
      )}
    </>
  );

  // ----- Facts strip data — 6 cells, prototype order -----
  const sc = scorecardQuery.data;

  const budgetRollup = engagement.budget_rollup;
  const openIssues = engagement.open_issues_count;
  const recentSignals = engagement.recent_signals;

  // Go-Live date + days-to-go (prototype: `goLiveDate` + `daysToGo`).
  // T-0128 added `planned_end` to the Project schema; surface it if
  // present, otherwise pending.
  // The date typed on the engagement wins; otherwise the go-live date the
  // contract commits to. When both exist and differ, the tile says so.
  const contractGoLive = factValue(contractFacts, 'go_live_date');
  const plannedEnd = engagement.planned_end ?? contractGoLive ?? null;
  const goLiveFromContract = !engagement.planned_end && contractGoLive !== null;
  const goLiveDiffers =
    !!engagement.planned_end &&
    contractGoLive !== null &&
    engagement.planned_end !== contractGoLive;
  const goLiveDate = plannedEnd ? new Date(plannedEnd) : null;
  const today = new Date();
  const daysToGo =
    goLiveDate && !Number.isNaN(goLiveDate.getTime())
      ? Math.round((goLiveDate.getTime() - today.getTime()) / 86_400_000)
      : null;
  const goLiveValue =
    goLiveDate && !Number.isNaN(goLiveDate.getTime()) ? formatDate(plannedEnd) : '—';
  const daysToGoText =
    daysToGo === null ? null : daysToGo >= 0 ? `in ${daysToGo}d` : `${Math.abs(daysToGo)}d ago`;
  const goLiveMeta =
    daysToGoText === null
      ? 'No planned go-live date yet'
      : `${daysToGoText} · ${
          goLiveFromContract
            ? 'from the contract'
            : goLiveDiffers
              ? `contract says ${formatDate(contractGoLive)}`
              : 'target'
        }`;

  // Budget used (`ac / bac`) and variance vs plan (cost variance from
  // CPI: variance% = (1 - 1/cpi) so positive => over-plan).
  const budgetUsedPct =
    budgetRollup && budgetRollup.bac > 0
      ? Math.round((budgetRollup.ac / budgetRollup.bac) * 100)
      : null;
  const budgetVarPct =
    budgetRollup && budgetRollup.cpi > 0 ? (1 - 1 / budgetRollup.cpi) * 100 : null;
  const budgetValue = budgetUsedPct === null ? '—' : `${budgetUsedPct}%`;
  const contractValue = factValue(contractFacts, 'contract_value');
  const budgetVarText =
    budgetVarPct === null
      ? budgetRollup === null
        ? contractValue
          ? `Contract value ${contractValue} · no budget baseline yet`
          : 'No baseline yet'
        : budgetRollup.ac === 0
          ? 'No spend recorded yet'
          : 'Variance unavailable'
      : `${budgetVarPct >= 0 ? '+' : ''}${budgetVarPct.toFixed(1)}% vs. plan`;
  // Variance > 2% paints the meta line in the at-risk tone (matches the
  // prototype's `e.budgetVar > 0.02 ? var(--posture-at-risk) : ...`).
  const budgetMeta =
    budgetVarPct !== null && budgetVarPct > 2 ? (
      <span className="text-posture-at-risk">{budgetVarText}</span>
    ) : (
      budgetVarText
    );

  // Open · overdue. `open_issues_count` = open findings + deviations +
  // RFIs; `overdue_count` = past-due unresolved contract deliverables
  // (same rule as the portfolio column). Null only when talking to an API
  // build that predates the field.
  const overdueCount: number | null = engagement.overdue_count ?? null;

  // Partner / consultant. T-0128 added `client_pm`; consultant lookup is
  // not yet wired (would need a project-members endpoint). Pending state
  // is the explicit fallback rather than seeding a fake name.
  const partner = engagement.client_pm ?? null;

  // Pencil shown on the tiles the edit modal drives.
  const editAction = (label: string) => (
    <button
      type="button"
      onClick={() => setEditDetailsOpen(true)}
      aria-label={label}
      title={label}
      className="rounded-card p-0.5 text-ink-500 transition-colors hover:bg-paper-200 hover:text-ink-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600"
    >
      <Pencil className="h-3 w-3" aria-hidden="true" />
    </button>
  );

  // AI proposals — pending count from the routing queue. T-0122 added a
  // count endpoint hint via `decision=pending&limit=200`; the page
  // surfaces the bottom of the queue if there are more than 200, but the
  // overview KPI is satisfied by the same number the queue header uses.
  // The activity pulse carries the exact pending count (the queue list is
  // capped at 200 rows).
  const pendingProposalsCount = activityQuery.data?.pending_proposals ?? null;

  const phaseFact: FactItem = {
    label: 'Phase',
    value: currentPhase,
    meta: `from engagement status · ${titleCase(engagement.status)}`,
    source: 'Engagement status',
    action: editAction('Edit engagement status'),
  };
  const pocFact: FactItem = {
    label: 'Main POC',
    value: partner ?? 'Not set',
    meta: partner ? 'Client point of contact' : 'No main POC set',
    source: partner ? 'Engagement details' : 'Engagement details · not set',
    action: editAction('Edit main point of contact'),
  };

  const ivvFacts: FactItem[] = [
    {
      label: 'Go-Live',
      value: goLiveValue,
      meta: goLiveMeta,
      source: goLiveFromContract
        ? 'Contracted go-live'
        : plannedEnd
          ? 'Planned go-live'
          : 'Planned go-live · not set',
      action: editAction('Edit planned go-live date'),
    },
    phaseFact,
    pocFact,
    {
      label: 'Budget used',
      value: budgetValue,
      meta: budgetMeta,
      source: budgetRollup === null ? 'Budget · not set up' : 'Budget plan and actuals',
    },
    {
      label: 'Open · overdue',
      value: (
        <>
          {openIssues} <span className="text-meta font-normal text-ink-500">·</span>{' '}
          <span className="text-posture-critical">
            {overdueCount === null ? '—' : overdueCount}
          </span>
        </>
      ),
      meta: 'open issues · overdue deliverables',
      source: 'Open issues · overdue',
    },
    {
      label: 'AI proposals',
      value: activityQuery.isLoading
        ? '…'
        : pendingProposalsCount === null
          ? '—'
          : pendingProposalsCount,
      meta: (
        <Link
          to="/engagements/$engagementId/proposals"
          params={{ engagementId }}
          className="font-medium text-ai-ink underline decoration-ai-ink/40 underline-offset-2 hover:decoration-ai-ink"
        >
          Open review →
        </Link>
      ),
      source: 'AI proposals',
      variant: 'ai',
    },
  ];

  // Discovery and Selection: the schedule, the phase and the main POC. The
  // module figures moved to the stage track and the cards below.
  const startDate = engagement.start_date ?? null;
  const todayDay = todayIso();
  const schedule = scheduleOf(engagement.planned_end ?? null, todayDay);
  const scheduleFacts: FactItem[] = [
    {
      label: 'Schedule',
      value: schedule.text ? (
        <span className={schedule.overdue ? 'text-posture-critical' : undefined}>
          {schedule.text}
        </span>
      ) : (
        '—'
      ),
      meta:
        startDate && engagement.planned_end ? (
          <GoLiveRunway
            start={startDate}
            goLive={engagement.planned_end}
            today={todayDay}
            milestones={[]}
            startLabel="Start"
            endLabel="Planned end"
            className="mt-1 max-w-[380px]"
          />
        ) : engagement.planned_end ? (
          `Planned end ${formatDate(engagement.planned_end)} · no start date yet`
        ) : (
          'No planned end date yet'
        ),
      source:
        startDate && engagement.planned_end ? 'Engagement details' : 'Engagement details · not set',
      action: editAction('Edit the start and planned end dates'),
    },
    phaseFact,
    pocFact,
  ];

  const planInput: OverviewInput = {
    discoveryOn,
    selectionOn,
    summary: discoverySummary ?? null,
    signoff: signoffQuery.data ?? null,
    requirements: requirementsQuery.data ? requirementCounts : null,
    vi: viQuery.data ?? null,
    report: reportQuery.data ?? null,
    plannedEnd: engagement.planned_end ?? null,
    today: todayDay,
  };
  // Do next waits for every read it orders; one that failed counts as read.
  const settled = (q: { data?: unknown; error: unknown }, on: boolean) =>
    !on || q.data !== undefined || q.error != null;
  const planLoading = !(
    settled(discoveryQuery, discoveryOn) &&
    settled(requirementsQuery, selectionOn) &&
    settled(signoffQuery, signoffOn) &&
    settled(viQuery, viOn) &&
    settled(reportQuery, viOn)
  );
  const actions = planOn ? overviewActions(planInput) : [];
  const stages = planOn ? overviewStages(planInput) : [];
  const callbacks = {
    onEditDetails: () => setEditDetailsOpen(true),
    onCapture: handleCaptureSnapshot,
    onArchive: () => setArchiveOpen(true),
  };

  const moduleCards =
    discoveryOn || selectionOn ? (
      <div className={discoveryOn && selectionOn ? 'grid gap-4 lg:grid-cols-2' : 'grid gap-4'}>
        {discoveryOn ? <DiscoveryStatusCard engagementId={engagementId} /> : null}
        {selectionOn ? (
          <SelectionStatusCard engagementId={engagementId} discoveryOn={discoveryOn} />
        ) : null}
      </div>
    ) : null;

  return (
    <div className="space-y-6">
      <div>
        <EngagementHero
          engagementId={engagementId}
          projectId={engagement.project_id}
          clientName={engagement.client_name}
          firmName="Avero Advisors"
          serviceLabel={serviceLabel(engagement.modules_enabled)}
          engagementTitle={engagement.name}
          subtitle={subtitle}
          posture={ivvOn ? posture : undefined}
          bannerImageUrl={engagement.banner_image_url}
          onCaptureSnapshot={handleCaptureSnapshot}
          onGeneratePdf={latestSnapshotId ? handleGeneratePdf : undefined}
          generatingPdf={pdfMutation.isPending}
        />
        <FactsStrip items={ivvOn ? ivvFacts : scheduleFacts} />
        {pdfMutation.error ? (
          <div className="mt-2">
            <ErrorMessage error={pdfMutation.error} />
          </div>
        ) : null}
      </div>

      {ivvOn ? (
        <>
          {contractEnabled ? (
            <div className="space-y-4">
              <LatestPulseCard engagementId={engagementId} />
              <ContractAttentionPanel engagementId={engagementId} limit={4} />
            </div>
          ) : null}

          {moduleCards}

          <div className="grid gap-4 lg:grid-cols-[1fr_380px]">
            <HealthPostureGrid
              engagementId={engagementId}
              domains={postureQuery.data?.per_domain ?? []}
            />
            <div className="flex flex-col gap-4">
              <CriticalSignals signals={recentSignals} limit={8} />
              <SubmittalVerbsCard engagementId={engagementId} />
            </div>
          </div>

          <div className="grid gap-4 lg:grid-cols-[1fr_380px]">
            {sc ? (
              <ScorecardSummary engagementId={engagementId} scorecard={sc} />
            ) : (
              <section className="rounded-card border border-ink-100 bg-paper-0 p-card-pad">
                <h2 className="t-h3">Go-Live Readiness · weighted scorecard</h2>
                <p className="mt-2 text-body-sm text-ink-500">
                  {scorecardQuery.isLoading ? 'Loading scorecard…' : 'No scorecard available yet.'}
                </p>
              </section>
            )}
            <SnapshotsList
              snapshots={snapshotsQuery.data?.items ?? []}
              limit={5}
              onCaptureSnapshot={handleCaptureSnapshot}
              onOpenPdf={(snapshotId) => setPdfPreviewSnapshotId(snapshotId)}
            />
          </div>
        </>
      ) : (
        <>
          <DoNextCard
            engagementId={engagementId}
            actions={actions}
            loading={planLoading}
            callbacks={callbacks}
          />
          <StageTrack engagementId={engagementId} stages={stages} />

          {moduleCards}

          <div className="grid gap-4 lg:grid-cols-[1fr_380px]">
            <CriticalSignals signals={recentSignals} limit={8} />
            <SnapshotsList
              snapshots={snapshotsQuery.data?.items ?? []}
              limit={5}
              onCaptureSnapshot={handleCaptureSnapshot}
              onOpenPdf={(snapshotId) => setPdfPreviewSnapshotId(snapshotId)}
            />
          </div>
        </>
      )}

      <div className="flex items-center justify-end gap-2">
        {/* T-0161 — firm admins only; renders nothing for anyone else. */}
        <DeleteEngagementControl
          engagementId={engagementId}
          engagementName={engagement.name}
          clientName={engagement.client_name}
        />
        <Button type="button" variant="outline" size="sm" onClick={() => setEditDetailsOpen(true)}>
          <LayoutGrid className="h-4 w-4" aria-hidden="true" />
          <span>Details and modules</span>
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          data-open-archive={engagementId}
          onClick={() => setArchiveOpen(true)}
        >
          <Archive className="h-4 w-4" aria-hidden="true" />
          <span>Build engagement archive</span>
        </Button>
      </div>

      <ArchiveModal engagementId={engagementId} open={archiveOpen} onOpenChange={setArchiveOpen} />

      {captureOpen ? (
        <CaptureSnapshotModal
          engagementId={engagementId}
          fixedType="engagement"
          onClose={() => setCaptureOpen(false)}
          onGeneratePdf={(snapshotId) => {
            setCaptureOpen(false);
            setPdfPreviewSnapshotId(snapshotId);
          }}
        />
      ) : null}

      {editDetailsOpen ? (
        <EditEngagementDetailsModal
          engagementId={engagementId}
          projectId={engagement.project_id}
          initial={{
            status: engagement.status,
            start_date: engagement.start_date ?? null,
            planned_end: engagement.planned_end ?? null,
            client_pm: engagement.client_pm ?? null,
            modules_enabled: engagement.modules_enabled,
          }}
          onClose={() => setEditDetailsOpen(false)}
        />
      ) : null}

      {pdfPreviewSnapshotId ? (
        <PdfPreviewModal
          engagementId={engagementId}
          snapshotId={pdfPreviewSnapshotId}
          snapshotLabel={(() => {
            const snap = snapshotsQuery.data?.items?.find((s) => s.id === pdfPreviewSnapshotId);
            return snap
              ? `${titleCase(snap.snapshot_type)} report · seq ${snap.sequence_no}`
              : undefined;
          })()}
          open={pdfPreviewSnapshotId !== null}
          onOpenChange={(next) => {
            if (!next) setPdfPreviewSnapshotId(null);
          }}
        />
      ) : null}
    </div>
  );
}

export { OverviewPage };
